import { useTranslate } from 'core/hooks/useTranslate';
import { useEffect, useMemo, useRef, useState } from 'react';

export function useDialogAssignStudents({ students = [], onSuccess, onCancel, open }) {
  const { t } = useTranslate();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [studentsCount, setStudentsCount] = useState(0);
  const [initialSelectedIds, setInitialSelectedIds] = useState(new Set());
  const prevOpenRef = useRef(open);

  // Sync selectedStudents when students prop changes
  // Preserve selections for students that exist in the new list
  useEffect(() => {
    if (students.length === 0) {
      setSelectedStudents([]);
      return;
    }

    setSelectedStudents((prevSelected) => {
      // If no previous selection, use students as-is
      if (prevSelected.length === 0) {
        return students;
      }

      // Create a map of previous selections by ID
      const prevSelectionsById = new Map(prevSelected.map((s) => [s.id, s.selected]));

      // Merge: preserve selection state for existing students
      return students.map((student) => {
        const prevSelection = prevSelectionsById.get(student.id);
        // If student existed before, preserve their selection state
        // Otherwise, use the incoming selection state
        return prevSelection !== undefined ? { ...student, selected: prevSelection } : student;
      });
    });
  }, [students]);

  // Reset search and capture initial selection ONLY on open transition
  useEffect(() => {
    // Only capture when dialog transitions from closed to open
    if (open && !prevOpenRef.current) {
      setSearchTerm('');
      // Reset to current prop state (discard stale preserved selections from closed sessions)
      setSelectedStudents(students);
      // Capture the initial selected student IDs when dialog opens
      const initialIds = new Set(students.filter((s) => s.selected).map((s) => s.id));
      setInitialSelectedIds(initialIds);
    }
    prevOpenRef.current = open;
  }, [open, students]);

  useEffect(() => {
    const count = selectedStudents.filter((student) => student.selected).length;
    setStudentsCount(count);
  }, [selectedStudents]);

  // Compute hasChanges by comparing current selection with initial
  const hasChanges = useMemo(() => {
    const currentSelectedIds = new Set(selectedStudents.filter((s) => s.selected).map((s) => s.id));

    // Check if sets are equal
    if (currentSelectedIds.size !== initialSelectedIds.size) {
      return true;
    }

    for (const id of currentSelectedIds) {
      if (!initialSelectedIds.has(id)) {
        return true;
      }
    }

    return false;
  }, [selectedStudents, initialSelectedIds]);

  const handleToggle = (studentId) => {
    setSelectedStudents((prevStudents) => prevStudents.map((student) => (student.id === studentId ? { ...student, selected: !student.selected } : student)));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSave = () => {
    if (onSuccess) {
      onSuccess(selectedStudents);
    }
  };

  const handleCancel = () => {
    // Reset to original students when canceling
    setSelectedStudents(students);
    setSearchTerm('');
    // Reset initial selection to match current students prop
    setInitialSelectedIds(new Set(students.filter((s) => s.selected).map((s) => s.id)));
    if (onCancel) {
      onCancel();
    }
  };

  const filteredStudents = selectedStudents.filter((student) => student.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return {
    t,
    filteredStudents,
    searchTerm,
    studentsCount,
    hasChanges,
    handleToggle,
    handleSearchChange,
    handleSave,
    handleCancel,
  };
}
